import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult, Operation } from '@apollo/client';
import * as Sentry from '@sentry/browser';

const BACKEND_RELEASE_HEADER = 'X-Bilderit-Release';
const BACKEND_RELEASE_TAG = 'backend_release';

let lastSeen: string | undefined;

/**
 * Reads the `X-Bilderit-Release` response header (exposed by the backend's
 * CORS middleware, see `packages/backend/graphql/src/modules/shared/graphql/cors.ts`)
 * and records it as a Sentry tag, so client-side error reports answer "which
 * backend served this?" — covering 100% of requests, unlike sampled tracing.
 *
 * This is a fire-and-forget diagnostic: it must never delay, block, or break
 * the GraphQL response, so every read is guarded.
 */
function recordBackendRelease(operation: Operation): void {
  try {
    const response = operation.getContext().response as Response | undefined;
    const release = response?.headers?.get(BACKEND_RELEASE_HEADER);
    if (!release) return;
    if (release === lastSeen) return;
    lastSeen = release;
    Sentry.getCurrentScope().setTag(BACKEND_RELEASE_TAG, release);
  } catch {
    // Diagnostic only — never let a failure here affect the data path.
  }
}

export const backendReleaseLink = new ApolloLink((operation, forward) => {
  // Subscribe rather than .map(): map only runs for emitted results, so it
  // would miss the error channel — and error responses (4xx/5xx, where
  // HttpLink has already stored the Response on the context before parsing
  // fails) are exactly the ones whose backend release we most want tagged.
  return new Observable<FetchResult>((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => {
        recordBackendRelease(operation);
        observer.next(result);
      },
      error: (error) => {
        recordBackendRelease(operation);
        observer.error(error);
      },
      complete: () => observer.complete(),
    });
    return () => subscription.unsubscribe();
  });
});
